/**
 * SOW template admin request/response contracts
 * ──────────────────────────────────────────────
 * Zod schemas for the admin endpoints that edit, clone and publish a SOW
 * document template (workspace.template, key='deal:sow'). Imported by both the
 * API (request validation) and the web editor (request building / typing).
 *
 * The `contract` field is typed loosely here (a JSON object); the authoritative
 * structural validation is `documentContractSchema` in @protoss/doc, run on the
 * API before persisting. The `previewSample` is the editor's mock dataset,
 * stored additively in `workspace.template.metadata.previewSample`.
 */
import { z } from 'zod';
export const previewSampleSchema = z.object({
    version: z.literal('1.0').default('1.0'),
    /** Binding token path (e.g. `context.clientName`) → sample display value. */
    tokens: z.record(z.string()).default({}),
    /** dynamicBlock dataKey → sample rows (row-scope key → cell value). */
    data: z.record(z.array(z.record(z.string()))).default({}),
    /** contentBlock dataKey (e.g. `narrative.scope_of_work`) → sample markdown. */
    narrative: z.record(z.string()).default({}),
});
export const updateSowTemplateRequestSchema = z.object({
    contract: z.record(z.unknown()),
    previewSample: previewSampleSchema.optional(),
    label: z.string().min(1).optional(),
    /**
     * slot → workspace-asset-uuid map persisted in metadata.assetSlots, resolving the
     * contract's {{protoss:asset:<slot>}} references at export. Sent by the Cover & Brand
     * panel when a logo swap re-points a slot; merged into existing metadata when present.
     */
    assetSlots: z.record(z.string()).optional(),
    /**
     * Optimistic-concurrency guard: the `version` the client loaded. When present and it no
     * longer matches the row (a co-editor saved in between), the API returns 409 STALE_WRITE
     * instead of silently clobbering. Omit to force last-write-wins (e.g. an explicit overwrite).
     */
    expectedVersion: z.string().optional(),
});
export const createSowTemplateRequestSchema = z.object({
    sourceTemplateUuid: z.string().uuid(),
    label: z.string().min(1),
    contract: z.record(z.unknown()).optional(),
    previewSample: previewSampleSchema.optional(),
});
export const sowTemplateSummarySchema = z.object({
    uuid: z.string(),
    label: z.string(),
    version: z.string(),
    status: z.enum(['draft', 'published', 'archived']),
    isDefault: z.boolean(),
    scope: z.enum(['system', 'workspace', 'user']),
});
//# sourceMappingURL=sow-template-admin.js.map